// Live exchange rates from Frankfurter (ECB reference rates): free, no API key,
// CORS-friendly. The engine itself never fetches; we hand it a rate table.
//
// The engine wants the value of one unit of each currency in the primary
// currency. Frankfurter returns the opposite (units of each currency per one
// unit of the base), so we invert.

const SUPPORTED = ["GBP", "EUR", "USD", "AUD"];
const CACHE_PREFIX = "vulpari_fx_";

interface CachedTable {
  date: string;
  rates: Record<string, number>;
}

/** Last cached rate table for a primary currency, if any (used for an instant,
 * offline-friendly first paint before the live fetch returns). */
export function cachedRates(primary: string): Record<string, number> | null {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + primary);
    if (!raw) {
      return null;
    }
    return (JSON.parse(raw) as CachedTable).rates;
  } catch {
    return null;
  }
}

/** Fetch live rates for the given primary currency and cache them. Throws on a
 * network or parse failure so the caller can fall back to the cache. */
export async function fetchRates(primary: string): Promise<Record<string, number>> {
  const symbols = SUPPORTED.filter((code) => code !== primary).join(",");
  const url = `https://api.frankfurter.dev/v1/latest?base=${encodeURIComponent(primary)}&symbols=${symbols}`;
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Exchange-rate request failed: ${response.status}`);
  }
  const data = (await response.json()) as { date: string; rates: Record<string, number> };

  const rates: Record<string, number> = { [primary]: 1 };
  for (const [code, perPrimary] of Object.entries(data.rates)) {
    if (perPrimary > 0) {
      rates[code] = 1 / perPrimary;
    }
  }

  try {
    localStorage.setItem(CACHE_PREFIX + primary, JSON.stringify({ date: data.date, rates }));
  } catch {
    // A full or unavailable localStorage is not fatal; we just lose the cache.
  }
  return rates;
}
